// Text styles, fields, and typography helpers.
import { StyleSheet, TextInput, View, type TextStyle } from "react-native";
import { EllipsizeMode, TranslatableText, useTranslation } from "./translation";
import { colorAmbiguousLightGrey, colorBorderPrimary, colorBorderWarning, colorGreyHover, colorGreyHoverBorder, colorSurfacePrimary, colorSurfaceSecondary, colorTextDisabled, colorTextPrimary, colorTextWarning } from "./color";
import { HorizBox, Pad } from "./basics";
import React, { useEffect, useState } from "react";
import { Checkmark } from "@carbon/icons-react";
import { WebLinkBase } from "platform-specific/link";
import { useConfig } from "util/features";
import { getFontLineHeight, getFontSize } from "platform-specific/font";
import { nullA, nullComponent, undefA, undefB, undefN, undefR, undefS, t, undefTL, undefFP, makeTestID } from "util/stdtypes";
import { StandardConfig } from "util/stdconfig";
import { getTheme } from "config/theme";

const theme = getTheme()
const themeFontFamilies = theme?.fontFamilies;

export const fontFamilySansRegular = themeFontFamilies?.fontFamilySansRegular ?? 'IBMPlexSans_400Regular, Arial, Helvetica, sans-serif';
export const fontFamilySansMedium = themeFontFamilies?.fontFamilySansMedium ?? 'IBMPlexSans_500Medium, Arial, Helvetica, sans-serif';
export const fontFamilySansSemiBold = themeFontFamilies?.fontFamilySansSemiBold ?? 'IBMPlexSans_600SemiBold, Arial, Helvetica, sans-serif';
export const fontFamilyMonoRegular = themeFontFamilies?.fontFamilyMonoRegular ?? 'IBMPlexMono_400Regular, Arial, Helvetica, sans-serif';
export const fontFamilyMonoMedium = themeFontFamilies?.fontFamilyMonoMedium ?? 'IBMPlexMono_500Medium, Arial, Helvetica, sans-serif';
export const fontFamilyMonoSemiBold =themeFontFamilies?.fontFamilyMonoSemiBold ?? 'IBMPlexMono_600SemiBold, Arial, Helvetica, sans-serif';

export type TextType = 'large' | 'small' | 'tiny';
export type TextWeight = 'regular' | 'medium' | 'strong';

export function Heading({text=undefS, weight='strong' as TextWeight, type='small' as TextType, color=colorTextPrimary, center=false, label = undefTL, underline=false, formatParams=undefFP}) {
    const weightMap = {
        regular: fontFamilySansRegular,
        medium: fontFamilySansMedium,
        strong: fontFamilySansSemiBold,
    } 
    const sizeMap = {
        large: HeadingStyle.large,
        small: HeadingStyle.small,
    }
    const ariaMap = {
        large: '1',
        small: '2',
    }
    return <TranslatableText text={text} label={label} aria-level={ariaMap[type]} role='heading' formatParams={formatParams} 
        style={[
            {fontFamily: weightMap[weight]},
            sizeMap[type],
            underline && {textDecorationLine: 'underline'},
            center ? {textAlign: 'center'} : null, {color}
        ]}
    />
}

// HACK: React Native non-web doesn't support non-numeric line heights
const HeadingStyle = StyleSheet.create({
    large: {
        fontSize: getFontSize(24),
        lineHeight: getFontLineHeight(24),
    },
    small: {
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16),
    },
})

export type CardHeadingType = 'large' | 'small'

export function CardHeading({text=undefS, type='large' as CardHeadingType, color=colorTextPrimary, center=false, label = undefTL, underline=false, formatParams=undefFP}) {
    const sizeMap = {
        large: CardHeadingStyle.large,
        small: CardHeadingStyle.small,
    }
    const ariaMap = {
        large: '1',
        small: '2',
    }
    return <TranslatableText text={text} label={label} aria-level={ariaMap[type]} role='heading' formatParams={formatParams}
        style={[
            {fontFamily: fontFamilySansMedium},
            sizeMap[type],
            underline && {textDecorationLine: 'underline'},
            center ? {textAlign: 'center'} : null,
            {color}
        ]}
    />
}

const CardHeadingStyle = StyleSheet.create({
    large: {
        fontSize: getFontSize(28),
        lineHeight: getFontLineHeight(28),
    },
    small: {
        fontSize: getFontSize(18),
        lineHeight: getFontLineHeight(18),
    },
})

export function DataVizText({type='large' as TextType, text = undefS, label = undefTL, formatParams = undefFP, color=colorTextPrimary}) {
    const s = DataVizTextStyle;
    const styleMap = {
        large: s.large,
        small: s.small,
    }
    return <TranslatableText text={text} role='heading' label={label} formatParams={formatParams}
        style={[styleMap[type], {color}]} />
}
const DataVizTextStyle = StyleSheet.create({
    large: {
        fontFamily: fontFamilySansSemiBold,
        fontSize: getFontSize(18),
        lineHeight: getFontLineHeight(18),
    },
    small: {
        fontFamily: fontFamilyMonoRegular,
        fontSize: getFontSize(12),
        lineHeight: getFontLineHeight(12),
    },
})


export function DEPRECATED_EditorialHeading({type='large' as TextType, text = undefS, label = undefTL, underline = undefB, formatParams = undefFP, color=colorTextPrimary, italic=false}) {
    const s = DEPRECATED_EditorialHeadingStyle;
    const styleMap = {
        large: s.large,
        small: s.small,        
    }    
    return <TranslatableText text={text} label={label} formatParams={formatParams}
        style={[
            styleMap[type], 
            {color}, 
            italic && {fontStyle: 'italic'},
            underline && {textDecorationLine: 'underline'}
        ]} />
}
const DEPRECATED_EditorialHeadingStyle = StyleSheet.create({
    large: {
        fontFamily: fontFamilySansRegular,
        fontSize: getFontSize(24),
    },
    small: {
        fontFamily: fontFamilySansRegular,
        fontSize: getFontSize(18),
    }

})


export function Paragraph({numberOfLines=undefN, type='small' as TextType, color=colorTextPrimary, strong=false, text = undefS, label = undefTL, formatParams = undefFP, underline=false}) {
    const s = ParagraphStyle;
    const styleMap = {
        large: s.largeParagraph,
        small: s.smallParagraph
    }
    return <TranslatableText numberOfLines={numberOfLines} text={text} label={label} formatParams={formatParams} role='paragraph' 
        style={[
            styleMap[type], {color}, 
            strong && {fontFamily: fontFamilySansSemiBold},
            underline && {textDecorationLine: 'underline'}
        ]} 
    />
}


const ParagraphStyle = StyleSheet.create({
    largeParagraph: {
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16, 1.5),
        fontFamily: fontFamilySansRegular,
    },
    smallParagraph: {
        fontSize: getFontSize(14),
        lineHeight: getFontLineHeight(14, 1.5),
        fontFamily: fontFamilySansRegular,
    },
})



export function UtilityText({type='small', center=false, right=false, 
    text=undefS, label = undefTL, formatParams=undefFP, color=colorTextPrimary, 
    weight='regular', caps=false, underline=false, 
    numberOfLines=undefN, ellipsizeMode='tail' as EllipsizeMode, 
    italic=false, mono=false, ...props
}) {
    const s = UtilityTextStyle;
    const styleMap = {
        large: s.utilityLarge,
        small: s.utilitySmall,
        tiny: s.utilityTiny,
    }
    const weightMap = {
        regular: fontFamilySansRegular,
        medium: fontFamilySansMedium,
        strong: fontFamilySansSemiBold,
    }
    const monoWeightMap = {
        regular: fontFamilyMonoRegular,
        medium: fontFamilyMonoMedium,
        strong: fontFamilyMonoSemiBold,
    }    
    if (!text && !label) return null;
    return <TranslatableText text={text} label={label} role="paragraph" formatParams={formatParams}
        numberOfLines={numberOfLines} ellipsizeMode={ellipsizeMode}
        style={[
            styleMap[type], {color}, 
            italic && {fontStyle: 'italic'},
            underline && {textDecorationLine: 'underline'},
            weight && {fontFamily: mono ? monoWeightMap[weight] : weightMap[weight]},
            center && {textAlign: 'center'},
            right && {textAlign: 'right'},
            caps && {textTransform: 'uppercase', letterSpacing: 0.5}
        ]} {...props} />
}

const UtilityTextStyle = StyleSheet.create({
    utilityLarge: {
        fontSize: getFontSize(16), 
        lineHeight: getFontLineHeight(16),
    },
    utilitySmall: {
        fontSize: getFontSize(14), 
        lineHeight: getFontLineHeight(14),
    },
    utilityTiny: {
        fontSize: getFontSize(12),
        lineHeight: getFontLineHeight(12),
    },
})


export function WebLink({url, newTab=false, style=nullA, setHover=undefA, testID=undefS, children, onLinkPress=undefA, linkType=undefS}) {
    const { openLinksInNewTab } = useConfig<StandardConfig>();
    
    return <WebLinkBase 
        newTab={openLinksInNewTab || newTab} style={style}
        setHover={setHover} onLinkPress={onLinkPress}
        testID={testID}
        url={url}
        linkType={linkType}>
        {children}
    </WebLinkBase>
}

export function LinkText({type='small' as TextType, testID = undefS, color=colorTextPrimary, text = undefS, url = undefS, label = undefTL, formatParams = undefFP}) {
    return <WebLink url={url} testID={testID} newTab>
        <UtilityText underline type={type} text={text} label={label} formatParams={formatParams} color={color}/>
    </WebLink>
}

export function WebLinkTextButton({label = undefTL, heading=undefB, text=undefS, color=colorTextPrimary, url=undefS, type='small' as TextType, mono=false, weight='regular' as TextWeight, numberOfLines=undefN, onLinkPress=undefA, linkType=undefS}) {
    const s = WebLinkTextButtonStyle;
    const [hover, setHover] = useState(false);
    return <WebLink testID={makeTestID({label, text})} url={url} onLinkPress={onLinkPress} linkType={linkType}
        style={{... (hover ? s.hoverButton : s.button), color}} setHover={setHover}>
        {heading ? 
            <Heading label={label} text={text} color={color} type={type} weight={weight} /> 
            : 
            <UtilityText label={label} text={text} color={color} type={type} weight={weight} mono={mono} numberOfLines={numberOfLines}/>
        }
    </WebLink>
}
const WebLinkTextButtonStyle = StyleSheet.create({
    button: {
        textDecorationLine: 'none',
        overflow: 'hidden',
        ...{textOverflow: 'ellipsis'} as any
    },
    hoverButton: {
        textDecorationLine: 'none',
        overflow: 'hidden',
        opacity: 0.7,
        ...{textOverflow: 'ellipsis'} as any
    }
})

// HACK: This is a hack to allow the test to access the onChangeText function
// In theory it should be possible to do this with dispatchEvent on the DOM
// node, but that doesn't seem to work in current versions of react-native-web
export const global_textinput_test_handlers: Record<string, (value: string) => void> = {};

export function AutoSizeTextInput({value, onChange, placeholder, style, hoverStyle=undefR, maxHeight = 400, emptyHeight = 54, testID, multiline = true, ariaLabel, ...props}) {
    const [height, setHeight] = useState(0);
    const [hover, setHover] = useState(false);

    useEffect(() => {
        if (testID) {
            global_textinput_test_handlers[testID] = onChange;
        }
        return () => {
            if (testID) {
                delete global_textinput_test_handlers[testID];
            }
        }
    }, []);

    useEffect(() => {
        if (value == '' && height > 0) {
            setHeight(emptyHeight);
        }
    }, [value])

    function onContentSizeChange(e) {
        const newHeight = e.nativeEvent.contentSize.height;
        if (newHeight > height) {
            setHeight(Math.min(newHeight, maxHeight));
        }
    }

    const styleHeight = Math.max(emptyHeight, height);

    // HACK: Mobile doesn't support onMouseEnter/onMouseLeave, so we use onMouseEnter/onMouseLeave as any
    return <View style={{height: styleHeight}}>
        <TextInput value={value} onChangeText={onChange} 
            placeholder={placeholder} 
            placeholderTextColor={colorAmbiguousLightGrey}
            {...{onMouseEnter: () => setHover(true), 
                onMouseLeave: () => setHover(false)} as any}
            aria-label={ariaLabel}
            multiline={multiline} 
            testID={testID}
            style={[style, {height: styleHeight, color: colorTextPrimary}, hover ? hoverStyle : null]} 
            onContentSizeChange={onContentSizeChange} {...props} />
    </View>
}

export type TextFieldSize = 'small' | 'large';

export function TextField({value, icon=nullComponent, error=false, big=false, autoFocus=false, 
    noTranslationPlaceholder = undefS, placeholder = undefTL, testID = undefS, placeholderParams = undefR, 
    onChange = undefA, onFocusChange = undefA, multiline, ariaLabel = undefTL, 
    size='large' as TextFieldSize, editable = undefB, ...props
}) {
    const s = TextFieldStyle;
    const tPlaceholder = useTranslation(placeholder, placeholderParams);
    const tAriaLabel = useTranslation(ariaLabel);
    const small = size === 'small';
    const emptyHeight = size === 'small' ? 40 : big ? 192 : 54;
    return <View>
        <View>
            <AutoSizeTextInput value={value ?? ''} onChange={onChange} 
                emptyHeight={emptyHeight} autoFocus={autoFocus} testID={testID}
                style={[s.textField, 
                    error ? {borderColor: colorBorderWarning} : null, 
                    icon ? {paddingLeft: small ? 42 : 48} : null,
                    small ? {height: 40} : null,
                    editable === false ? {backgroundColor: colorSurfaceSecondary, color: colorTextDisabled} : undefined,
                ]} 
                hoverStyle={s.hover}
                placeholder={noTranslationPlaceholder ?? tPlaceholder} placeholderTextColor={colorAmbiguousLightGrey} 
                onFocus={() => onFocusChange?.(true)} onBlur={() => onFocusChange?.(false)}
                multiline={multiline}
                ariaLabel={tAriaLabel}
                editable={editable}
                {...props}
            />
            {icon && React.createElement(icon, {
                size: 24, color: colorTextPrimary, 
                style: {
                    position: 'absolute', 
                    top: small ? 10 : 15, 
                    left: 16, 
                    zIndex: 100,
                    ...(small ? {width: 20, height: 20} : null)
                }})}
        </View>
    </View>
}

const TextFieldStyle = StyleSheet.create({
    textField: {
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 15,
        fontSize: getFontSize(16),
        lineHeight:getFontLineHeight(16, 1.5),
        fontFamily: fontFamilySansRegular,
        color: colorTextPrimary,
    },
    hover: {
        borderColor: colorGreyHoverBorder
    }
})

export function CharacterCounter({max, min, text}) {
    const count = text?.length ?? 0;

    if (count == 0) {
        return <UtilityText type='tiny' weight='medium' label={t('{min} characters min')} formatParams={{min}} 
            color={colorAmbiguousLightGrey} />
    } else if (count < min) {
        return <UtilityText type='tiny' weight='medium' label={t('{count}/{min} characters min')} formatParams={{count,min}} 
            color={colorAmbiguousLightGrey} />
    } else if (count > max) {
        return <UtilityText type='tiny' weight='medium' label={t('{count}/{max} characters max')} formatParams={{count,max}} 
            color={colorTextWarning} />
    } else {
        return <HorizBox>
            <Checkmark color={colorTextPrimary}/>
            <Pad size={8} />
            <UtilityText type='tiny' weight='medium' label={t('{count}/{max} characters max')} formatParams={{count,max}} />
        </HorizBox>
    }
}

export function checkValidLength({text, min, max}) {
    const count = text?.length ?? 0;
    return count >= min && count <= max;
}

export function CircleCount({count}) {
    const s = CircleCountStyle;
    return <View style={s.circle}>
        <UtilityText weight='medium' text={String(count)} />
    </View>
}
const CircleCountStyle = StyleSheet.create({
    circle: {
        backgroundColor: colorGreyHover,
        borderRadius: 100,
        width: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center'
    }
});

export function BulletText({ label = undefTL, text=undefS, color=colorTextPrimary }) {
    return <HorizBox>
        <UtilityText text='•' color={color}/>
        <Pad size={10} />
        <UtilityText label={label} text={text} color={color}/>
    </HorizBox>
}